import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { toast } from "sonner";
import { Send, Truck } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function OrdersTab({ orders, isLoading, getStatusBadge, formatDate, fetchAllData }) {
  const [freightDialogOpen, setFreightDialogOpen] = useState(null);
  const [freightOrigin, setFreightOrigin] = useState("Guangzhou, China");
  const [freightDest, setFreightDest] = useState("");
  const [freightWeight, setFreightWeight] = useState(150);
  const [freightMethod, setFreightMethod] = useState("sea");

  const updateOrderStatus = async (orderId, status, opts = {}) => {
    try { await axios.patch(`${API}/orders/${orderId}/status`, { status, ...opts }); toast.success("Updated"); fetchAllData(); } catch (e) { toast.error("Failed"); }
  };

  const createFreightQuote = async (orderId) => {
    try { await axios.post(`${API}/freight/quotes`, { order_id: orderId, origin: freightOrigin, destination: freightDest, weight_kg: freightWeight, shipping_method: freightMethod }); toast.success("Freight quote created"); setFreightDialogOpen(null); fetchAllData(); } catch (e) { toast.error("Failed"); }
  };

  if (isLoading) return <div className="text-center py-12 text-zinc-500">Loading...</div>;
  if (orders.length === 0) return <div className="text-center py-12 text-zinc-500 bg-white rounded-sm border">No orders yet</div>;

  return (
    <div className="bg-white rounded-sm border border-zinc-200 overflow-hidden overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-zinc-50">
            <TableHead className="font-heading uppercase text-xs">Order #</TableHead>
            <TableHead className="font-heading uppercase text-xs">Customer</TableHead>
            <TableHead className="font-heading uppercase text-xs">Status</TableHead>
            <TableHead className="font-heading uppercase text-xs">PO Sent</TableHead>
            <TableHead className="font-heading uppercase text-xs">Date</TableHead>
            <TableHead className="font-heading uppercase text-xs">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((o) => (
            <TableRow key={o.id}>
              <TableCell className="font-mono">{o.order_number}</TableCell>
              <TableCell>
                <p className="font-medium">{o.customer_name}</p>
                <p className="text-sm text-zinc-500">{o.email}</p>
              </TableCell>
              <TableCell><Badge className={getStatusBadge(o.status)}>{o.status}</Badge></TableCell>
              <TableCell>{o.supplier_po_sent ? <Badge className="bg-green-100 text-green-800">Sent</Badge> : <Badge className="bg-gray-100 text-gray-800">Pending</Badge>}</TableCell>
              <TableCell className="text-sm text-zinc-500">{formatDate(o.created_at)}</TableCell>
              <TableCell>
                <div className="flex gap-2 flex-wrap">
                  {!o.supplier_po_sent && (
                    <Button variant="outline" size="sm" onClick={() => updateOrderStatus(o.id, "in_production", { send_supplier_po: true })}>
                      <Send className="h-4 w-4 mr-1" /> PO
                    </Button>
                  )}
                  <Dialog open={freightDialogOpen === o.id} onOpenChange={(open) => setFreightDialogOpen(open ? o.id : null)}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm"><Truck className="h-4 w-4 mr-1" /> Freight</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle className="font-heading uppercase">Freight Quote</DialogTitle></DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs uppercase">Origin</Label>
                            <Input value={freightOrigin} onChange={(e) => setFreightOrigin(e.target.value)} className="mt-1" />
                          </div>
                          <div>
                            <Label className="text-xs uppercase">Destination</Label>
                            <Input value={freightDest} onChange={(e) => setFreightDest(e.target.value)} className="mt-1" />
                          </div>
                          <div>
                            <Label className="text-xs uppercase">Weight (kg)</Label>
                            <Input type="number" value={freightWeight} onChange={(e) => setFreightWeight(parseFloat(e.target.value))} className="mt-1" />
                          </div>
                          <div>
                            <Label className="text-xs uppercase">Method</Label>
                            <Select value={freightMethod} onValueChange={setFreightMethod}>
                              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="sea">Sea</SelectItem>
                                <SelectItem value="air">Air</SelectItem>
                                <SelectItem value="express">Express</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Button onClick={() => createFreightQuote(o.id)} className="w-full bg-blue-600 hover:bg-blue-700">Create</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Select value={o.status} onValueChange={(v) => updateOrderStatus(o.id, v)}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_production">In Production</SelectItem>
                      <SelectItem value="qc_complete">QC Complete</SelectItem>
                      <SelectItem value="shipped">Shipped</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
